import React, { useEffect, useMemo, useState } from 'react'
import {
  Archive, Ban, Copy, FilePlus2, ListChecks, Loader2, Mails, Pencil, RefreshCw,
  Trash2, Users, X, XCircle
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiErrorMessage, communicationsService } from '../../services/communicationsService'
import type {
  MailLayout,
  NewsletterCampaign,
  NewsletterRecipient,
  NewsletterStats
} from '../../types/communications'
import CampaignEditor from './CampaignEditor'
import NewsletterContacts from './NewsletterContacts'

interface Props {
  notify?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SCHEDULED: 'bg-blue-100 text-blue-700',
  SENDING: 'bg-amber-100 text-amber-700',
  SENT: 'bg-emerald-100 text-emerald-700',
  CANCELLED: 'bg-red-100 text-red-700',
  ARCHIVED: 'bg-slate-100 text-slate-600'
}

const NewsletterManager: React.FC<Props> = ({ notify }) => {
  const { t } = useTranslation()
  const [tab, setTab] = useState<'campaigns' | 'contacts'>('campaigns')
  const [campaigns, setCampaigns] = useState<NewsletterCampaign[]>([])
  const [designs, setDesigns] = useState<MailLayout[]>([])
  const [stats, setStats] = useState<NewsletterStats | null>(null)
  const [editing, setEditing] = useState<{ campaign: NewsletterCampaign | null } | null>(null)
  const [log, setLog] = useState<{ campaign: NewsletterCampaign; rows: NewsletterRecipient[]; total: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')

  const load = async () => {
    const [rows, statRow] = await Promise.all([
      communicationsService.listCampaigns(),
      communicationsService.getNewsletterStats()
    ])
    setCampaigns(rows)
    setStats(statRow)
  }

  useEffect(() => {
    Promise.all([load(), communicationsService.getCampaignDesigns().then(setDesigns)])
      .catch(error => notify?.(apiErrorMessage(error, t('communications.newsletters.loadFailed')), 'error'))
      .finally(() => setLoading(false))
  }, [])

  // A campaign being sent changes on its own, so refresh while any is in flight.
  const inFlight = useMemo(
    () => campaigns.some(campaign => ['SCHEDULED', 'SENDING'].includes(campaign.status)),
    [campaigns]
  )
  useEffect(() => {
    if (!inFlight || editing) return
    const timer = window.setInterval(() => { load().catch(() => {}) }, 15000)
    return () => window.clearInterval(timer)
  }, [inFlight, editing])

  const act = async (key: string, action: () => Promise<unknown>, message: string) => {
    setBusy(key)
    try {
      await action()
      await load()
      notify?.(message, 'success')
    } catch (error) {
      notify?.(apiErrorMessage(error, t('communications.newsletters.actionFailed')), 'error')
    } finally {
      setBusy('')
    }
  }

  const openLog = async (campaign: NewsletterCampaign) => {
    setBusy(`log-${campaign.id}`)
    try {
      const result = await communicationsService.listCampaignRecipients(campaign.id)
      setLog({ campaign, rows: result.recipients, total: result.total })
    } catch (error) {
      notify?.(apiErrorMessage(error, t('communications.newsletters.reportFailed')), 'error')
    } finally {
      setBusy('')
    }
  }

  const targetSummary = (campaign: NewsletterCampaign) => {
    if (campaign.audience.customerIds.length) {
      return t('communications.newsletters.selectedCustomers', { count: campaign.audience.customerIds.length })
    }
    if (campaign.audience.sources.length === 1 && campaign.audience.sources[0] === 'PORTAL_USERS') {
      return t('communications.newsletters.source.PORTAL_USERS')
    }
    return t('communications.newsletters.everyone')
  }

  const designName = (key: string) => designs.find(design => design.key === key)?.name || key

  if (loading) return <div className="flex h-48 items-center justify-center"><Loader2 className="animate-spin text-blue-600" /></div>

  if (editing) return <CampaignEditor
    campaign={editing.campaign}
    designs={designs}
    notify={notify}
    onClose={() => { setEditing(null); load().catch(() => {}) }}
    onSaved={(campaign, options) => {
      setCampaigns(current => {
        const exists = current.some(row => row.id === campaign.id)
        return exists ? current.map(row => (row.id === campaign.id ? { ...row, ...campaign } : row)) : [campaign, ...current]
      })
      if (options?.close) { setEditing(null); load().catch(() => {}) }
    }} />

  const cards: Array<{ key: keyof NewsletterStats; icon: React.ElementType; tint: string }> = [
    { key: 'subscribed', icon: Mails, tint: 'text-emerald-600' },
    { key: 'unsubscribed', icon: XCircle, tint: 'text-red-600' },
    { key: 'suppressed', icon: Ban, tint: 'text-amber-600' },
    { key: 'knownContacts', icon: Users, tint: 'text-blue-600' }
  ]

  return <div className="space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex rounded-lg bg-gray-100 p-1">
        {(['campaigns', 'contacts'] as const).map(item => <button key={item} onClick={() => setTab(item)}
          className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
            tab === item ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'
          }`}>
          {t(`communications.newsletters.tabs.${item}`)}
        </button>)}
      </div>
      {tab === 'campaigns' && <button onClick={() => setEditing({ campaign: null })}
        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white">
        <FilePlus2 size={14} />{t('communications.newsletters.new')}
      </button>}
    </div>

    {stats && <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {cards.map(card => {
        const Icon = card.icon
        return <div key={card.key} className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase text-gray-400">
            <Icon size={13} className={card.tint} />{t(`communications.newsletters.stats.${card.key}`)}
          </div>
          <div className="mt-1 text-2xl font-semibold text-gray-900">{stats[card.key]}</div>
        </div>
      })}
    </div>}

    {tab === 'contacts' ? <NewsletterContacts notify={notify} /> : <section className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="w-full min-w-[860px] text-left text-xs">
        <thead className="border-b border-gray-100 text-[10px] font-bold uppercase text-gray-400">
          <tr>
            <th className="px-4 py-3">{t('communications.newsletters.columns.campaign')}</th>
            <th className="px-3 py-3">{t('communications.newsletters.columns.design')}</th>
            <th className="px-3 py-3">{t('communications.newsletters.columns.target')}</th>
            <th className="px-3 py-3">{t('communications.newsletters.columns.status')}</th>
            <th className="px-3 py-3">{t('communications.newsletters.columns.delivery')}</th>
            <th className="px-3 py-3">{t('communications.newsletters.columns.when')}</th>
            <th className="px-3 py-3" />
          </tr>
        </thead>
        <tbody>
          {!campaigns.length && <tr><td colSpan={7} className="p-8 text-center text-gray-400">
            {t('communications.newsletters.empty')}
          </td></tr>}
          {campaigns.map(campaign => {
            const subject = campaign.primaryLocale === 'fr' ? campaign.subjectFr : campaign.subjectEn
            const spinning = busy.endsWith(`-${campaign.id}`)
            return <tr key={campaign.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
              <td className="max-w-[240px] px-4 py-3">
                <div className="truncate font-semibold text-gray-800">{subject || campaign.name}</div>
                <div className="truncate text-[10px] text-gray-500">{campaign.name}</div>
              </td>
              <td className="px-3 py-3 text-gray-600">{designName(campaign.layoutKey)}</td>
              <td className="max-w-[160px] truncate px-3 py-3 text-gray-600">{targetSummary(campaign)}</td>
              <td className="px-3 py-3">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_COLORS[campaign.status] || STATUS_COLORS.DRAFT}`}>
                  {t(`communications.newsletters.status.${campaign.status}`)}
                </span>
              </td>
              <td className="px-3 py-3 text-gray-600">
                {campaign.recipientCount
                  ? <span>
                    {campaign.sentCount}/{campaign.recipientCount}
                    {Boolean(campaign.failedCount) && <span className="ml-1 text-red-600">
                      ({t('communications.newsletters.failedCount', { count: campaign.failedCount })})
                    </span>}
                  </span>
                  : <span className="text-gray-400">—</span>}
              </td>
              <td className="px-3 py-3 text-gray-500">
                {campaign.scheduledAt
                  ? new Date(campaign.scheduledAt).toLocaleString()
                  : campaign.createdAt ? new Date(campaign.createdAt).toLocaleDateString() : ''}
              </td>
              <td className="px-3 py-3">
                <div className="flex items-center justify-end gap-1">
                  {spinning && <Loader2 size={12} className="animate-spin text-blue-600" />}
                  <button onClick={() => setEditing({ campaign })} title={t(campaign.status === 'DRAFT'
                    ? 'communications.newsletters.edit' : 'communications.newsletters.view')}
                    className="rounded p-1.5 text-gray-500 hover:bg-blue-50 hover:text-blue-700">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => act(`clone-${campaign.id}`,
                    () => communicationsService.cloneCampaign(campaign.id), t('communications.newsletters.cloned'))}
                    disabled={Boolean(busy)} title={t('communications.newsletters.clone')}
                    className="rounded p-1.5 text-gray-500 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40">
                    <Copy size={13} />
                  </button>
                  {campaign.status !== 'DRAFT' && <button onClick={() => openLog(campaign)} disabled={Boolean(busy)}
                    title={t('communications.newsletters.deliveryLog')}
                    className="rounded p-1.5 text-gray-500 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40">
                    <ListChecks size={13} />
                  </button>}
                  {Boolean(campaign.failedCount) && ['SENT', 'SENDING', 'CANCELLED'].includes(campaign.status) &&
                    <button onClick={() => act(`retry-${campaign.id}`,
                      () => communicationsService.retryCampaign(campaign.id), t('communications.newsletters.retried'))}
                      disabled={Boolean(busy)} title={t('communications.newsletters.retryFailed')}
                      className="rounded p-1.5 text-gray-500 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-40">
                      <RefreshCw size={13} />
                    </button>}
                  {['SCHEDULED', 'SENDING'].includes(campaign.status) && <button
                    onClick={() => { if (window.confirm(t('communications.newsletters.cancelConfirm'))) {
                      act(`cancel-${campaign.id}`, () => communicationsService.cancelCampaign(campaign.id),
                        t('communications.newsletters.cancelled'))
                    } }}
                    disabled={Boolean(busy)} title={t('communications.newsletters.cancelAction')}
                    className="rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-40">
                    <XCircle size={13} />
                  </button>}
                  {['SENT', 'CANCELLED'].includes(campaign.status) && <button
                    onClick={() => act(`archive-${campaign.id}`,
                      () => communicationsService.archiveCampaign(campaign.id), t('communications.newsletters.archived'))}
                    disabled={Boolean(busy)} title={t('communications.newsletters.archive')}
                    className="rounded p-1.5 text-gray-500 hover:bg-slate-100 disabled:opacity-40">
                    <Archive size={13} />
                  </button>}
                  {campaign.status === 'DRAFT' && <button
                    onClick={() => { if (window.confirm(t('communications.newsletters.deleteConfirm'))) {
                      act(`delete-${campaign.id}`, () => communicationsService.deleteCampaign(campaign.id),
                        t('communications.newsletters.deleted'))
                    } }}
                    disabled={Boolean(busy)} title={t('communications.newsletters.delete')}
                    className="rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-40">
                    <Trash2 size={13} />
                  </button>}
                </div>
              </td>
            </tr>
          })}
        </tbody>
      </table>
    </section>}

    {log && <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[80vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <div>
            <h4 className="text-sm font-semibold">{t('communications.newsletters.deliveryLog')}</h4>
            <p className="text-[11px] text-gray-500">{log.campaign.name} · {t('communications.newsletters.rowCount', { count: log.total })}</p>
          </div>
          <button onClick={() => setLog(null)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"><X size={16} /></button>
        </header>
        <div className="max-h-[64vh] overflow-y-auto">
          {!log.rows.length && <p className="p-6 text-center text-xs text-gray-400">{t('communications.newsletters.noRecipients')}</p>}
          {log.rows.map(row => <div key={row.id}
            className="flex flex-wrap items-center gap-3 border-b border-gray-50 px-5 py-2 text-xs last:border-0">
            <span className="min-w-0 flex-1 truncate font-medium text-gray-800">{row.email}</span>
            <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase">{row.locale}</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
              row.status === 'DELIVERED' ? 'bg-emerald-100 text-emerald-700'
                : ['FAILED', 'SUPPRESSED'].includes(row.status) ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-600'
            }`}>{row.status}</span>
            {row.errorCode && <span className="text-[10px] text-red-600">{row.errorCode}</span>}
            <span className="text-[10px] text-gray-400">
              {row.sentAt ? new Date(row.sentAt).toLocaleString() : ''}
            </span>
          </div>)}
        </div>
      </div>
    </div>}
  </div>
}

export default NewsletterManager
